import { useEffect, useMemo, useRef, useState } from "react";
import { Button, Input } from "../lib/ui.js";
import { minToTimeLabel } from "../lib/availability.js";
import { formatTime, formatWeekdayDayMonthOnly } from "../lib/datetime.js";
import { addDaysYmd, minutesOfDayInZone, todayInZone, ymdOfInstant } from "../lib/zone.js";
import type { BookingApi, MyBooking } from "./api.js";
import { nextDayWithSlots, openBlocks, slotRange, startsByResource, type PublicConfig, type PublicResource, type Slot } from "./helpers.js";
import { useSlotDays } from "./useSlotDays.js";

/** Pixels per minute: 24 px per quarter hour, big enough for a finger. */
const PX_PER_MIN = 1.6;
const STEP_MIN = 15;

export interface PickedTime {
  resourceId: string;
  slot: Slot;
}

/**
 * The day view of the booking page, laid out like the portal's New booking schedule: a date bar (previous, a date box, next, today)
 * over a grid with one column per staff member and the hours down the side. Open times can be picked; everything else is hatched
 * (a customer is never told WHY a time is not free). Used for a new booking and for moving one.
 */
export function SlotCalendar({
  api,
  config,
  serviceIds,
  durationMin,
  columns,
  selected,
  onSelect,
  refreshKey = 0,
  mine = [],
  browse = false,
}: {
  api: BookingApi;
  config: PublicConfig;
  serviceIds: string[];
  durationMin: number;
  /** The staff who can do the chosen services: one column each. */
  columns: PublicResource[];
  selected: PickedTime | null;
  onSelect: (picked: PickedTime) => void;
  refreshKey?: number;
  /** The verified customer's own bookings, drawn on the day view so they can see what they already have. */
  mine?: MyBooking[];
  /** Show the day (every staff member, the customer's own bookings) even with no service chosen, but no free times: used once the customer is verified. */
  browse?: boolean;
}) {
  const zone = config.timezone;
  const today = todayInZone(zone);
  const [date, setDate] = useState(today);
  const { slots, loaded, loading, error, ensureDay, findNext, lastDay } = useSlotDays({ api, config, serviceIds, refreshKey });
  const jumped = useRef(false);
  const idsKey = serviceIds.join(",");

  // A new choice of services starts again at today and may jump ahead once to the first day that has a time.
  useEffect(() => {
    jumped.current = false;
    setDate(todayInZone(zone));
  }, [idsKey, refreshKey, zone]);

  useEffect(() => {
    ensureDay(date);
  }, [date, ensureDay]);

  const daySlots = useMemo(() => slots.filter((s) => ymdOfInstant(s.starts_at, zone) === date), [slots, zone, date]);
  const dayLoaded = loaded.has(date);

  // If today has nothing, show the first day that does (once; after that the customer is in charge of the date).
  useEffect(() => {
    if (jumped.current || !dayLoaded || daySlots.length > 0) return;
    jumped.current = true;
    const next = nextDayWithSlots(slots, zone, date);
    if (next) setDate(next);
  }, [dayLoaded, daySlots.length, slots, zone, date]);

  const starts = useMemo(() => startsByResource(daySlots, zone), [daySlots, zone]);
  const myToday = useMemo(() => mine.filter((b) => ymdOfInstant(b.starts_at, zone) === date), [mine, zone, date]);
  const range = useMemo(() => {
    const r = slotRange(slots, zone, durationMin);
    // Keep the customer's own bookings of the day inside the hours shown.
    for (const b of myToday) {
      r.start = Math.min(r.start, Math.floor(minutesOfDayInZone(b.starts_at, zone) / 60) * 60);
      r.end = Math.max(r.end, Math.min(24 * 60, Math.ceil(minutesOfDayInZone(b.ends_at, zone) / 60) * 60));
    }
    return r;
  }, [slots, zone, durationMin, myToday]);
  const [finding, setFinding] = useState(false);

  async function goNext() {
    setFinding(true);
    const next = await findNext(date);
    setFinding(false);
    if (next) setDate(next);
  }

  const setClamped = (ymd: string) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return;
    setDate(ymd < today ? today : ymd > lastDay ? lastDay : ymd);
  };

  return (
    <div>
      <div className="bk-card bk-datebar">
        <div className="bk-row" style={{ gap: 8 }}>
          <Button type="button" variant="ghost" disabled={date <= today} onClick={() => setClamped(addDaysYmd(date, -1))}>← Prev</Button>
          <Input type="date" aria-label="Day" value={date} min={today} max={lastDay} onChange={(e) => setClamped(e.target.value)} style={{ width: 160 }} />
          <Button type="button" variant="ghost" disabled={date >= lastDay} onClick={() => setClamped(addDaysYmd(date, 1))}>Next →</Button>
          <Button type="button" variant="ghost" disabled={date === today} onClick={() => setDate(today)}>Today</Button>
          <strong className="bk-dayname">{formatWeekdayDayMonthOnly(date)}</strong>
          {loading && <span className="bk-muted">Loading...</span>}
        </div>
        <div className="bk-legend" aria-hidden="true">
          <span><i className="bk-swatch bk-swatch-open" /> Available</span>
          <span><i className="bk-swatch bk-swatch-picked" /> Your choice</span>
          <span><i className="bk-swatch bk-swatch-closed" /> Not available</span>
          {mine.length > 0 && <span><i className="bk-swatch bk-swatch-mine" /> Your bookings</span>}
        </div>
      </div>

      {error && <p className="bk-error" role="alert">{error}</p>}

      {serviceIds.length === 0 && browse ? (
        <>
          <p className="bk-hint" style={{ margin: "0 0 8px" }}>Pick a service to see the times that are free.</p>
          <Grid zone={zone} date={date} columns={columns} starts={new Map()} range={range} durationMin={durationMin || 30} selected={null} onSelect={onSelect} mine={myToday} browse />
        </>
      ) : serviceIds.length === 0 ? (
        <div className="bk-empty">Pick a service to see the times that are free.</div>
      ) : columns.length === 0 ? (
        <div className="bk-empty">No one is available for that combination of services. Try fewer services.</div>
      ) : !dayLoaded ? (
        <div className="bk-empty">Looking for times...</div>
      ) : daySlots.length === 0 ? (
        <div className="bk-empty">
          <p style={{ margin: "0 0 10px" }}>No times are free on {formatWeekdayDayMonthOnly(date)}.</p>
          {date < lastDay && <Button type="button" variant="primary" disabled={finding} onClick={() => void goNext()}>{finding ? "Looking..." : "Show the next day with a time"}</Button>}
        </div>
      ) : (
        <Grid zone={zone} date={date} columns={columns} starts={starts} range={range} durationMin={durationMin} selected={selected} onSelect={onSelect} mine={myToday} />
      )}
    </div>
  );
}

function Grid({
  zone,
  date,
  columns,
  starts,
  range,
  durationMin,
  selected,
  onSelect,
  mine,
  browse = false,
}: {
  browse?: boolean;
  mine: MyBooking[];
  zone: string;
  date: string;
  columns: PublicResource[];
  starts: Map<string, Map<number, Slot>>;
  range: { start: number; end: number };
  durationMin: number;
  selected: PickedTime | null;
  onSelect: (picked: PickedTime) => void;
}) {
  const [hover, setHover] = useState<{ resourceId: string; minute: number } | null>(null);
  const total = range.end - range.start;
  const hours: number[] = [];
  for (let m = Math.ceil(range.start / 60) * 60; m <= range.end; m += 60) hours.push(m);
  const y = (minute: number) => (minute - range.start) * PX_PER_MIN;

  const selectedHere = selected && ymdOfInstant(selected.slot.starts_at, zone) === date ? selected : null;

  return (
    <div className="bk-grid rc-scrollbar" role="group" aria-label="Times by staff member">
      <div style={{ display: "flex", minWidth: columns.length * 112 + 56 }}>
        <div className="bk-axis">
          <div className="bk-colhead" />
          <div style={{ position: "relative", height: total * PX_PER_MIN }}>
            {hours.map((m) => (
              <span key={m} className="bk-hour" style={{ top: y(m) - 7 }}>{minToTimeLabel(m)}</span>
            ))}
          </div>
        </div>
        {columns.map((col) => {
          const byMinute = starts.get(col.id) ?? new Map<number, Slot>();
          const blocks = openBlocks([...byMinute.keys()], durationMin);
          const hoverHere = hover && hover.resourceId === col.id ? hover.minute : null;
          const pickedMinute = selectedHere && selectedHere.resourceId === col.id ? minutesOfDayInZone(selectedHere.slot.starts_at, zone) : null;
          return (
            <div key={col.id} className="bk-col">
              <div className="bk-colhead">{col.name}</div>
              <div className={`bk-colbody${browse ? " bk-colbody-browse" : ""}`} style={{ height: total * PX_PER_MIN }}>
                {hours.map((m) => (
                  <div key={m} className="bk-hourline" style={{ top: y(m) }} />
                ))}
                {blocks.map((b) => (
                  <div key={b.startMin} className="bk-openblock" style={{ top: y(b.startMin), height: (b.endMin - b.startMin) * PX_PER_MIN }} />
                ))}
                {mine
                  .filter((b) => b.resource_id === col.id)
                  .map((b) => {
                    const from = minutesOfDayInZone(b.starts_at, zone);
                    const to = minutesOfDayInZone(b.ends_at, zone);
                    return (
                      <div key={b.id} className="bk-mine" style={{ top: y(from), height: Math.max(2, (to - from) * PX_PER_MIN - 2) }} title={`Your booking: ${b.services.join(" + ")}`}>
                        <strong>Your booking</strong>
                        <span>{formatTime(b.starts_at)} {b.services.join(" + ")}{b.guest_name ? ` (${b.guest_name})` : ""}</span>
                      </div>
                    );
                  })}
                {hoverHere != null && !(pickedMinute != null && hoverHere < pickedMinute + durationMin && hoverHere + durationMin > pickedMinute) && (
                  <div className="bk-preview" style={{ top: y(hoverHere), height: durationMin * PX_PER_MIN }}>{minToTimeLabel(hoverHere)}</div>
                )}
                {pickedMinute != null && (
                  <div className="bk-picked" style={{ top: y(pickedMinute), height: durationMin * PX_PER_MIN }}>{minToTimeLabel(pickedMinute)}</div>
                )}
                {[...byMinute.entries()].map(([minute, slot]) => (
                  <button
                    key={minute}
                    type="button"
                    className="bk-start"
                    style={{ top: y(minute), height: STEP_MIN * PX_PER_MIN }}
                    aria-label={`${col.name}, ${formatTime(slot.starts_at)}`}
                    aria-pressed={pickedMinute === minute}
                    onClick={(e) => {
                      e.currentTarget.blur(); // no lingering focus state (it interfered with the next swipe on some phones)
                      onSelect({ resourceId: col.id, slot });
                    }}
                    // The hover preview is for a mouse only: on a touch screen it would re-draw during a swipe and get stuck after a tap.
                    onPointerEnter={(e) => e.pointerType === "mouse" && setHover({ resourceId: col.id, minute })}
                    onPointerLeave={() => setHover(null)}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
